import { isFunction } from 'lodash';

import { VisuallyHidden } from '@/components/visually-hidden';
import { tv } from '@/utils/styles';

interface Props {
  name: string;
  legend: string;
  onLabel?: string;
  offLabel?: string;
  on: boolean;
  disabled?: boolean;
  onChange?: (state: boolean) => void;
}

const Toggle = ({ name, legend, onLabel, offLabel, on, disabled = false, onChange }: Props) => {
  const handleOnClick = (state: boolean): void => {
    if (!disabled && isFunction(onChange)) onChange(state);
  };

  const hasLabels = !onLabel && !offLabel;

  const idOn = `toggle-on-${name}`;
  const idOff = `toggle-off-${name}`;

  const { fieldset, label, button, buttonIndicator, input } = toggleStyles({ on, disabled, hasLabels });

  return (
    <fieldset className={fieldset()}>
      <VisuallyHidden as="legend">{legend}</VisuallyHidden>
      <div>
        <input
          type="radio"
          id={idOff}
          name={name}
          checked={!on}
          disabled={disabled}
          onChange={() => handleOnClick(false)}
          className={input()}
        />
        {Boolean(offLabel) && (
          <label htmlFor={idOff} className={label()}>
            {offLabel}
          </label>
        )}
      </div>
      {/* This inside element needs to be clickable but isn't wanted as a
          button element due to the other radio inputs being accessible */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <span className={button()} onClick={() => handleOnClick(!on)}>
        <span className={buttonIndicator()} />
      </span>
      <div>
        <input
          type="radio"
          id={idOn}
          name={name}
          checked={!!on}
          disabled={disabled}
          onChange={() => handleOnClick(true)}
          className={input()}
        />
        {Boolean(onLabel) && (
          <label htmlFor={idOn} className={label()}>
            {onLabel}
          </label>
        )}
      </div>
    </fieldset>
  );
};

const toggleStyles = tv({
  slots: {
    fieldset: 'group flex items-center',
    input: 'toggle-input peer absolute left-[-900em] appearance-none',
    label:
      'relative mr-1 flex cursor-pointer p-1 text-baseSm leading-none text-grey-400 transition-colors duration-150 hover:text-text-primary peer-checked:text-text-primary',
    button:
      'relative mr-1 block h-6 w-11 cursor-pointer items-center rounded-md border border-grey-300 bg-white shadow-softer group-hover:border-grey-400',
    buttonIndicator:
      'absolute top-0.5 block h-[18px] w-[18px] translate-x-0.5 rounded bg-lightBlue-500 transition-all duration-100 ease-in-out',
  },
  variants: {
    on: {
      true: {
        buttonIndicator: 'translate-x-[22px]',
      },
    },
    disabled: {
      true: {
        fieldset: 'pointer-events-none',
        button: 'cursor-default border-grey-300',
        buttonIndicator: 'bg-lightBlue-200',
        label: 'cursor-default !text-grey-400',
      },
    },
    hasLabels: {
      true: {},
      false: {},
    },
  },
  compoundSlots: [
    {
      slots: ['buttonIndicator'],
      hasLabels: true,
      on: false,
      class: 'opacity-60 grayscale',
    },
  ],
});

export { Toggle };
